import type { ReactNode } from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { DataTable, EmptyState, LoadingOrbit } from '@chatsdet/ui';
import { Play } from '@phosphor-icons/react';
import type { RunsListRow } from '../../../../features/workflows/useWorkflowTestExecutions';

interface TestRunsListSearch {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface TestRunsListPagination {
  totalPages: number;
  totalItems: number;
}

interface TestRunsListProps {
  columns: ColumnDef<StockFeatures, RunsListRow>[];
  executions?: RunsListRow[];
  hasEligibleWorkflows: boolean;
  hasRunnableExternalSuites: boolean;
  isLoading: boolean;
  pagination?: TestRunsListPagination;
  runnableNoun: string;
  searchParams: TestRunsListSearch;
  selectedRunId?: string | null;
  /** Passed through to `DataTable` — squares off the top corners when fused with the filter/sparkline lockup above. */
  containerClassName?: string;
  buildRunHref: (execution: RunsListRow) => string | undefined;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortChange: (sortBy: string | undefined, sortOrder: 'asc' | 'desc' | undefined) => void;
  renderNewRunButton: (size: 'sm' | 'md') => ReactNode;
}

export function TestRunsList({
  columns,
  executions,
  hasEligibleWorkflows,
  hasRunnableExternalSuites,
  isLoading,
  pagination,
  runnableNoun,
  searchParams,
  selectedRunId,
  containerClassName,
  buildRunHref,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  renderNewRunButton,
}: TestRunsListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingOrbit size="lg" />
      </div>
    );
  }

  if (executions?.length && pagination) {
    return (
      <DataTable
        columns={columns}
        data={executions}
        aria-label="Test runs table"
        containerClassName={containerClassName}
        serverPagination={{
          page: searchParams.page,
          pageSize: searchParams.pageSize,
          pageCount: pagination.totalPages,
          totalItems: pagination.totalItems,
          onPageChange,
          onPageSizeChange,
        }}
        serverSorting={{
          sortBy: searchParams.sortBy,
          sortOrder: searchParams.sortOrder,
          onSortChange,
        }}
        rowClassName={(row) =>
          row.original.id === selectedRunId
            ? 'bg-accent/50 transition-colors duration-150'
            : 'transition-colors duration-150'
        }
        getRowHref={buildRunHref}
      />
    );
  }

  return (
    <EmptyState
      icon={<Play size={48} />}
      title="No test runs yet"
      description={
        <>
          Test runs execute all your {runnableNoun} workflows as regression tests, capturing
          pass/fail results and filing issues for failures.
          {!hasEligibleWorkflows && !hasRunnableExternalSuites && (
            <>
              <br />
              Approve and pass a workflow first, then start a test run.
            </>
          )}
          {!hasEligibleWorkflows && hasRunnableExternalSuites && (
            <>
              <br />
              You can also run a configured external test suite.
            </>
          )}
        </>
      }
      action={
        hasEligibleWorkflows || hasRunnableExternalSuites ? renderNewRunButton('sm') : undefined
      }
    />
  );
}
